const baseUrl = window.location.origin;
//const baseUrl = "https://saasmw.alkhwarizmi.xyz/api/WebOrder";
const BASE_URL = `${baseUrl}/api/WebOrder/functions/v1`;

//const BASE_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

function headers(token?: string | null): Record<string, string> {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: ANON_KEY,
    Authorization: token ? `Bearer ${token}` : `Bearer ${ANON_KEY}`,
  };
  return h;
}

// Known country codes sorted by length (longest first) to match correctly
const KNOWN_COUNTRY_CODES = [
  "966",  // Saudi Arabia
  "971",  // UAE
  "965",  // Kuwait
  "968",  // Oman
  "974",  // Qatar
  "973",  // Bahrain
  "962",  // Jordan
  "20",   // Egypt
  "961",  // Lebanon
  "963",  // Syria
  "964",  // Iraq
  "212",  // Morocco
  "213",  // Algeria
  "216",  // Tunisia
  "218",  // Libya
  "249",  // Sudan
  "967",  // Yemen
  "1",    // USA/Canada
  "44",   // UK
  "33",   // France
  "49",   // Germany
  "91",   // India
  "92",   // Pakistan
  "90",   // Turkey
];

function deriveCountryCode(phoneNumber?: string): string | undefined {
  if (!phoneNumber) return undefined;
  const trimmed = phoneNumber.trim().replace(/^\+/, "");
  
  // Try to match known country codes
  for (const code of KNOWN_COUNTRY_CODES) {
    if (trimmed.startsWith(code)) {
      return `+${code}`;
    }
  }
  
  // Fallback: take first 3 digits (most common country code length)
  const match = trimmed.match(/^(\d{1,3})/);
  return match ? `+${match[1]}` : undefined;
}

export interface SendOTPResponse {
  success: true;
  data: {
    request_id: string;
    expires_in: number;
    otp_length: number;
    is_new_user: boolean;
    masked_number: string;
  };
}

export interface VerifyOTPResponse {
  success: true;
  data: {
    token: string;
    is_new_user: boolean;
    customer: CustomerData;
  };
}

export interface CustomerData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  mobile: string;
  country_code?: string | null;
  points_balance: number;
  tier: string;
  addresses: any[];
  vehicles: any[];
}

export interface APIError {
  success: false;
  error: {
    code: string;
    message: string;
    retry_after?: number;
    remaining_attempts?: number;
    locked_until?: number;
  };
}

async function request<T>(
  url: string,
  options: RequestInit,
): Promise<T | APIError> {
  try {
    const res = await fetch(url, options);
    const json = await res.json();
    return json;
  } catch {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message: "Connection error. Please try again.",
      },
    };
  }
}

export async function sendOTP(
  countryCode: string,
  mobile: string,
  restaurantId?: string,
): Promise<SendOTPResponse | APIError> {
  return request(`${BASE_URL}/auth-send-otp`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ country_code: countryCode, mobile, restaurant_id: restaurantId }),
  });
}

export async function verifyOTP(
  requestId: string,
  countryCode: string,
  mobile: string,
  otp: string,
  restaurantId?: string,
): Promise<VerifyOTPResponse | APIError> {
  return request(`${BASE_URL}/auth-verify-otp`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({
      request_id: requestId,
      country_code: countryCode,
      mobile,
      otp,
      restaurant_id: restaurantId,
    }),
  });
}

export async function completeProfile(
  token: string,
  firstName: string,
  lastName: string,
  phoneNumber: string,
  email?: string,
  restaurantId?: string,
  countryCode?: string,
): Promise<{ success: true; data: { customer: CustomerData } } | APIError> {
  const resolvedCountryCode = countryCode || deriveCountryCode(phoneNumber);

  return request(`${BASE_URL}/update-user-profile`, {
    method: "PUT",
    headers: headers(token),
    body: JSON.stringify({
      first_name: firstName,
      last_name: lastName,
      mobile: phoneNumber,
      country_code: resolvedCountryCode,
      ...(email ? { email } : {}),
      restaurant_id: restaurantId,
    }),
  });
}

export interface UserProfileResponse {
  success: true;
  data: {
    customer: CustomerData & {
      loyalty?: {
        points: number;
        tier: string;
        tier_ar: string;
        next_tier: string;
        next_tier_ar: string;
        points_to_next: number;
        total_to_next: number;
        recent_earnings: { order_id: string; points: number; date: string }[];
      };
      recent_orders?: {
        id: string;
        order_number: string;
        order_type: string;
        status: string;
        total: number;
        date: string;
        payment_method: string;
        items: {
          menu_item_id: string;
          name: string;
          quantity: number;
          price: number;
        }[];
      }[];
    };
  };
}

export async function getUserProfile(
  token: string,
  mobile?: string,
  restaurantId?: string,
): Promise<UserProfileResponse | APIError> {
  const url = new URL(`${BASE_URL}/get-user-profile`);
  if (mobile) url.searchParams.set("mobile", mobile);
  if (restaurantId) url.searchParams.set("restaurant_id", restaurantId);
  return request(url.toString(), {
    method: "GET",
    headers: headers(token),
  });
}

export interface UpdateProfilePayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  country_code?: string;
  addresses?: any[];
}

export async function updateUserProfile(
  token: string,
  phone: string,
  payload: UpdateProfilePayload,
  restaurantId?: string,
): Promise<{ success: true; data: { customer: CustomerData } } | APIError> {
  const url = new URL(
    `${BASE_URL}/update-user-profile?phone=${encodeURIComponent(phone)}`,
  );
  const resolvedCountryCode = payload.country_code || deriveCountryCode(phone);

  return request(url.toString(), {
    method: "PUT",
    headers: headers(token),
    body: JSON.stringify({ ...payload, country_code: resolvedCountryCode, mobile: phone, restaurant_id: restaurantId }),
  });
}

export async function logoutAPI(token: string, restaurantId?: string): Promise<void> {
  try {
    await fetch(`${BASE_URL}/auth-logout`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify({ restaurant_id: restaurantId }),
    });
  } catch {
    // silent
  }
}
